/**
 * O que se resolve pelo documento, nas áreas de baixo da mesa (E203.F5.T09, ADR-0146).
 *
 * Os formulários que o painel do documento abre (a conclusão da CCB, os pontos para revisão) saem
 * das áreas de baixo: fica o resumo e, com pendência, o aviso que leva à seção do documento onde o
 * dado está impresso (lá, o clique na linha abre o painel). "Abrir aqui" continua mostrando o
 * formulário de sempre, para o caminho que o documento ainda não cobre (inventário da T08).
 *
 * E205.F5.T02: com pendência, o formulário já vem aberto ("Abrir aqui" vira o conteúdo); "Recolher"
 * fecha.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';

const t = ptBR.mesaUnica.noDocumento;

export interface NoDocumentoProps {
  id: string;
  resumo: string;
  pendente: boolean;
  /** A seção do documento onde o dado está; `null` quando o documento não o imprime. */
  ancora: string | null;
  children: ReactNode;
}

export function NoDocumento({
  id,
  resumo,
  pendente,
  ancora,
  children,
}: NoDocumentoProps): JSX.Element {
  // E205.F5.T02 (ADR-0147 §2.7): com pendência, o conteúdo já vem aberto (a aba é o lugar de
  // resolver) e fica aberto quando ela se resolve — o aviso do que foi gravado continua à vista; sem
  // pendência, recolhido. A escolha do perito vale daí em diante.
  const [escolha, setEscolha] = useState<boolean | null>(pendente ? true : null);
  useEffect(() => {
    if (pendente) setEscolha((atual) => atual ?? true);
  }, [pendente]);
  const aqui = escolha ?? false;
  return (
    <div
      id={id}
      className="mt-sc-4 scroll-mt-sc-6 space-y-sc-3 border border-border p-sc-4"
      data-testid={`no-documento-${id}`}
      data-pendente={pendente}
    >
      <div className="flex flex-wrap items-center gap-sc-2 text-body-sm">
        <span
          className={pendente ? 'font-semibold text-fg' : 'text-fg-muted'}
          data-testid={`no-documento-${id}-resumo`}
        >
          {resumo}
        </span>
        {pendente && ancora !== null ? (
          <Link
            to={{ hash: ancora }}
            className="text-accent-700"
            data-testid={`no-documento-${id}-ir`}
          >
            {t.irAoDocumento}
          </Link>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={aqui}
          onClick={() => setEscolha(!aqui)}
          data-testid={`no-documento-${id}-aqui`}
        >
          {aqui ? t.recolher : t.abrirAqui}
        </Button>
      </div>
      {aqui ? children : null}
    </div>
  );
}
